//
/**
 * Returns true if a value is an object
 * @return {*|boolean}
 * @author FM
 */
export const isObject = e =>
    e ? 'object' === typeof e && Object === e.constructor : false;

/**
 * Returns true if is a object type (any constructor) but is not an array
 * @return {*|boolean}
 */
export const isObjectAndNotIsArray = e =>
    e ? 'object' === typeof e && !Array.isArray(e) : false;

/**
 * Returns true if object is empty
 * @return {*|boolean}
 * @author FM
 */
export const isFalsyOrIsEmptyObject = e =>
    !e || (isObject(e) && 0 === Object.keys(e).length);

/**
 * Converts a color object to a string representation in RGB notation
 * If the input is not an object, it returns the input as-is
 * @param {Object|string} item - The input color object with numeric values, or a css rgb value string
 * @returns {string} - The RGB notation if the input is an object, otherwise the input itself
 */
export const toRGBnotation = item => {
    if (item && isObject(item)) {
        const validate = e => (e ? (e < 0 ? 0 : e > 255 ? 255 : e) : 0);
        return `rgb(${
            validate(item.r) // 0-255
        },${
            validate(item.g) // 0-255
        },${
            validate(item.b) // 0-255
        })`;
    }
    return item; // todo: condider instead a fallback value eg.: rgb(0,0,0)
};

/**
 * Description: Group objects by a specific property into a result object
 * @param {Array} arr - array of objects
 * @param {String} key - the key property
 * @returns {Object} - result
 * @example
 * const cars = [
 *   { brand: "Audi", color: "black" },
 *   { brand: "Audi", color: "white" },
 *   { brand: "Audi", color: "white" },
 *   { brand: "Ferarri", color: "red" },
 *   { brand: "Ford", color: "white" },
 *   { brand: "Ford", color: "blue" },
 *   { brand: "Peugot", color: "white" }
 * ];
 * console.log("group", groupObjectsByProperty(cars, "brand"));
 * // result:
 * {
 *   Audi: (3) [{…}, {…}, {…}]
 *   Ferarri: [{…}]
 *   Ford: (2) [{…}, {…}]
 *   Peugot: [{…}]
 * }
 */
export const groupObjectsByProperty = (arr, key) => {
    return arr.reduce((obj, item) => {
        const value = item[key];
        if (value in obj) {
            obj[value].push(item);
        } else {
            obj[value] = [item];
        }
        return obj;
    }, Object.create(null));
};

/**
 * Updates or adds a "timestamp" property to the provided object with the current time
 * @param {Object} obj - The object to be updated or modified
 * @param {number} [time=Date.now()] - The timestamp value to assign (default: current time)
 * @returns {Object} - The updated object with the timestamp property
 */
export const timeSign = (obj, time = Date.now()) => {
    if (obj) {
        const key = 'timestamp';
        if (key in obj) {
            const descriptor = Object.getOwnPropertyDescriptor(obj, key);
            if (descriptor.writable) {
                obj[key] = time;
            } else {
                console.warn(`${key} is not writable @`, obj);
            }
        } else {
            Object.defineProperty(obj, key, {
                enumerable: false,
                writable: true,
                value: time
            });
        }
    }
    return obj;
};

/**
 * Compares two objects based on the specified key and returns the comparison result
 * @param {Object} obj1 - The first object to compare
 * @param {Object} obj2 - The second object to compare
 * @param {string} key - The key to use for comparison
 * @returns {number} - The comparison result (-1|1|0)
 * ┌─────┬───────────────┐
 * │  -1 │ obj1 < obj2   │
 * ├─────┼───────────────┤
 * │   1 │ obj1 > obj2   │
 * ├─────┼───────────────┤
 * │   0 │ obj1 = obj2   │
 * └─────┴───────────────┘
 */
export const sortABCompareKey = (obj1, obj2, key) => {
    const a = obj1[key];
    const b = obj2[key];
    return a !== b ? (a > b ? 1 : -1) : 0;
};

export const compareObjectsByValueOf_OrderKey = (a, b) =>
    sortABCompareKey(a, b, 'order');

/**
 * Checks if two objects have the same value for a given key once it is converted to an integer
 * @param {Object} obj1
 * @param {Object} obj2
 * @param {string} key
 * @returns {boolean} - true if both have the same integer value for the given key, false otherwise
 */
export const sameKeyAsInt = (obj1, obj2, key) => {
    const a = obj1?.[key];
    if (a) {
        const b = obj2?.[key];
        if (b) {
            const f = parseInt;
            return f(a) === f(b);
        }
    }
    return false;
};

/**
 * Checks if two objects are not the same and have different string representations.
 * @param {object} obj1 - The first object to compare.
 * @param {object} obj2 - The second object to compare.
 * @returns {boolean} - Returns `true` if the objects have different string representations.
 */
export const objectsDiffer = (obj1, obj2) => {
    return obj1 !== obj2 && JSON.stringify(obj1) !== JSON.stringify(obj2);
};

/**
 * Creates a deep copy of a value that can be modified independently of the original source.
 * @param {*} obj - possible object to be serializes as JSON string and parsed back
 * @returns {*} - deep copy of the original object, or falsy value.
 */
export const deepCopy = obj => obj && JSON.parse(JSON.stringify(obj));
